import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios, { AxiosError } from 'axios';
import { User } from '@shared/types';
import { env } from '@shared/config/env';

const API_URL = env.apiUrl;

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  loginWithGoogle: () => void;
  loginWithGithub: () => void;
  getAccessToken: () => Promise<string | undefined>;
  updateUser: (userData: Partial<User>) => void;
}

interface SyncResponse {
  success: boolean;
}

interface MeResponse {
  user: User;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [appUser, setAppUser] = useState<User | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const shouldForceLogout = useCallback((error: unknown): boolean => {
    if (!error) { return false; }

    const authError = error as {
      error?: string;
      message?: string;
      error_description?: string;
      statusCode?: number;
    };

    const messages = [authError.error, authError.message, authError.error_description]
      .filter((msg): msg is string => typeof msg === 'string')
      .map((msg) => msg.toLowerCase());

    const tokenErrorKeywords = [
      'invalid refresh token',
      'unknown or invalid refresh token',
      'invalid_grant',
      'login_required',
      'consent_required',
      'session_missing',
    ];

    if (messages.some((msg) => tokenErrorKeywords.some((keyword) => msg.includes(keyword)))) {
      return true;
    }

    const axiosError = error as AxiosError;
    if (axiosError?.response?.status && [401, 403].includes(axiosError.response.status)) {
      const url = axiosError?.config?.url || '';
      if (url.includes('auth0.com') || url.includes('/oauth/token')) {
        return true;
      }
    }

    if (authError.statusCode && [401, 403].includes(authError.statusCode)) {
      return true;
    }

    return false;
  }, []);

  const forceLogout = useCallback(async () => {
    if (isLoggingOut) { return; }
    setIsLoggingOut(true);
    setAppUser(null);
    try {
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    } finally {
      setIsLoggingOut(false);
    }
  }, [auth0Logout, isLoggingOut]);

  // Sync Auth0 user to our database when authenticated
  useEffect(() => {
    const syncUser = async () => {
      if (auth0IsAuthenticated && auth0User && !syncing && !appUser) {
        setSyncing(true);
        try {
          let token: string | undefined;
          try {
            token = await getAccessTokenSilently();
          } catch (tokenError) {
            console.error('Failed to obtain access token while syncing user:', tokenError);
            if (shouldForceLogout(tokenError)) {
              await forceLogout();
              return;
            }
            throw tokenError;
          }

          // Check if we already have the user data in memory to avoid redundant calls
          if (appUser) {
            setSyncing(false);
            return;
          }

          // Call our backend sync endpoint
          const response = await axios.post<SyncResponse>(
            `${API_URL}/auth/sync`,
            {
              auth0_id: auth0User.sub,
              email: auth0User.email,
              name: auth0User.name,
              picture: auth0User.picture,
              created_at: auth0User.updated_at, // Auth0's created_at equivalent
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data.success) {
            // Fetch full user data from our API
            const userResponse = await axios.get<MeResponse>(`${API_URL}/auth/me`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            setAppUser(userResponse.data.user);
          }
        } catch (error) {
          console.error('Failed to sync user with backend:', error);

          // If we get an Auth0 refresh token / token error, automatically log the user out
          if (shouldForceLogout(error)) {
            await forceLogout();
            return;
          }
        } finally {
          setSyncing(false);
        }
      }
    };

    // Only run sync if we don't have the appUser yet
    if (auth0IsAuthenticated && !appUser && !syncing) {
      void syncUser();
    }
  }, [auth0IsAuthenticated, auth0User, getAccessTokenSilently, appUser, syncing, forceLogout, shouldForceLogout]);

  const login = useCallback(() => {
    void loginWithRedirect({
      authorizationParams: {
        screen_hint: 'login',
      },
    });
  }, [loginWithRedirect]);

  const loginWithGoogle = useCallback(() => {
    void loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2',
      },
    });
  }, [loginWithRedirect]);

  const loginWithGithub = useCallback(() => {
    void loginWithRedirect({
      authorizationParams: {
        connection: 'github',
      },
    });
  }, [loginWithRedirect]);

  const logout = useCallback(async () => {
    await forceLogout();
  }, [forceLogout]);

  const getAccessToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch (error: unknown) {
      console.error('Failed to get access token:', error);

      if (shouldForceLogout(error)) {
        await forceLogout();
      }

      return undefined;
    }
  }, [getAccessTokenSilently, shouldForceLogout, forceLogout]);

  const updateUser = useCallback((userData: Partial<User>) => {
    setAppUser((prev) => (prev ? { ...prev, ...userData } : prev));
  }, []);

  const value = {
    user: appUser,
    isAuthenticated: auth0IsAuthenticated,
    // Loading if Auth0 is loading or we are actively syncing
    // Note: We don't wait for appUser because if sync fails, we still want to show the app
    loading: auth0IsLoading || syncing,
    login,
    logout,
    loginWithGoogle,
    loginWithGithub,
    getAccessToken,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
